// Schedule › To schedule › Asked to move (F-08), Blueprint's (BL-189): customers who asked, from their
// visit's link, for a different time or to cancel, each a RowList row that opens its job, its one action at
// rest and the rest under More; Move it is a FormDialog. With nobody asking it is a line, not a section
// (at most three sections in view). What it does is use-visit-requests.ts's, as Riverstone's
// VisitRequests.tsx.
import { useState } from 'react';
import {
  FormDialog,
  Notice,
  RowList,
  SelectField,
  Skeleton,
  SkeletonGroup,
  type Row,
  type RowAction,
} from '@flowology/ui-web/fl';
import { Section } from '@flowology/ui-web/patterns';
import { DAY_WINDOWS, dayOptions, today as todayIn, windowLabel } from '../book-model.ts';
import { openJob } from '../job/job-page.ts';
import { MIRROR_LINE, useMirror } from '../shared/mirror.ts';
import { useSaveKey } from '../shared/save-key.ts';
import { phoneText } from '../types.ts';
import { askedText, requestChip, windowForPart, type VisitRequest } from './tobook-model.ts';
import { rowTone } from './to-schedule-model.ts';
import { moveVisit, requestError, useVisitRequests } from './use-visit-requests.ts';

export function AskedToMove({
  timeZone,
  canMove: mayMove,
  onChanged,
}: {
  timeZone: string;
  /** jobs.dispatch: move or cancel the visit here (others call, keep, or open the job). */
  canMove: boolean;
  onChanged: () => void;
}) {
  // While the company mirrors its old system its visits are moved and cancelled there; a request is
  // still kept or closed here, which changes only the request (BL-088)
  const mirroring = useMirror();
  const canMove = mayMove && !mirroring;
  const { list, error, toast, busy, keep, closeCancelled, cancelVisit, moved } = useVisitRequests(onChanged);
  const [moving, setMoving] = useState<VisitRequest | null>(null);

  const dialog = (
    <MoveDialog
      request={moving}
      timeZone={timeZone}
      onClose={() => setMoving(null)}
      onMoved={(r, when) => moved(r, when)}
    />
  );
  if (list && !list.length && !toast && !error && !moving)
    return <p className="bp-tobook-quiet">No one is asking to move or cancel a visit.</p>;
  return (
    <Section title="Asked to move" {...(list ? { count: list.length } : {})}>
      {mirroring && mayMove && <p className="bp-tobook-sub">{MIRROR_LINE}</p>}
      {toast && <Notice tone="done" title={toast} />}
      {error && (
        <p className="bp-tobook-bad" role="alert">
          {error}
        </p>
      )}
      {list === null ? (
        !error && (
          <SkeletonGroup label="Loading the requests">
            <Skeleton shape="row" width="full" />
            <Skeleton shape="row" width="full" />
          </SkeletonGroup>
        )
      ) : list.length ? (
        <RowList
          label="Asked to move"
          rows={list.map((r): Row => {
            const chip = requestChip(r);
            const gone = r.visit.status === 'cancelled';
            // the job's only visit can't be cancelled on its own: the job is, on its sheet (QA-532)
            const withJob = !gone && r.kind === 'cancel' && r.visit.onlyVisit;
            const off = busy === r.id;
            const acts: (RowAction | false | '' | null)[] = [
              gone && {
                id: 'close',
                text: 'Close the request',
                isDisabled: off,
                onPress: () => void closeCancelled(r),
              },
              !gone &&
                r.kind === 'change' &&
                canMove && { id: 'move', text: 'Move it', onPress: () => setMoving(r) },
              withJob &&
                canMove && {
                  id: 'cancel-job',
                  text: `Cancel job #${r.visit.jobNumber}…`,
                  onPress: () => openJob(r.visit.jobId),
                },
              !gone &&
                !withJob &&
                r.kind === 'cancel' &&
                canMove && {
                  id: 'cancel',
                  text: off ? 'Cancelling…' : 'Cancel the visit',
                  isDisabled: off,
                  onPress: () => void cancelVisit(r),
                },
              r.customer.phone && {
                id: 'call',
                text: `Call ${phoneText(r.customer.phone)}`,
                href: `tel:${r.customer.phone}`,
              },
              !gone && { id: 'keep', text: 'Keep the visit', isDisabled: off, onPress: () => void keep(r) },
            ];
            return {
              id: r.id,
              name: r.customer.name,
              label: `${r.customer.name}: ${chip.text}`,
              onOpen: () => openJob(r.visit.jobId),
              openLabel: `${r.customer.name}: open job #${r.visit.jobNumber}`,
              end: { word: chip.text, tone: rowTone(chip.tone) },
              lines: [
                { text: `Booked: ${r.visit.whenText} · job #${r.visit.jobNumber} · ${r.visit.summary}` },
                ...(r.kind === 'change' ? [{ items: r.times.map((t) => t.text), label: 'Their picks' }] : []),
                ...(r.note ? [{ text: `“${r.note}”`, tone: 'ink' as const, size: 'sm' as const }] : []),
                {
                  text: `${askedText(r.createdAt, timeZone)} ${
                    gone
                      ? 'The visit is cancelled already: close the request.'
                      : withJob
                        ? 'It is the job’s only visit, so cancel the job: its sheet asks why.'
                        : 'Their visit stays booked until you act.'
                  }`,
                },
              ],
              actions: acts.filter((a): a is RowAction => !!a),
            };
          })}
        />
      ) : (
        <p className="bp-tobook-sub">No one is asking to move or cancel a visit.</p>
      )}
      {dialog}
    </Section>
  );
}

/** "Move it": the visit to one of their picks (or another day), then the request is closed. */
function MoveDialog({
  request,
  timeZone,
  onClose,
  onMoved,
}: {
  request: VisitRequest | null;
  timeZone: string;
  onClose: () => void;
  onMoved: (r: VisitRequest, when: string) => void;
}) {
  const today = todayIn(timeZone);
  const days = dayOptions(today, 60).slice(1);
  const first = request?.times[0];
  const start = { date: first?.date ?? days[0]!.date, win: first ? windowForPart(first.part) : 0 };
  // the dialog's fields start from the request each time it opens
  const [opened, setOpened] = useState<string | null>(null);
  const [date, setDate] = useState(start.date);
  const [win, setWin] = useState(start.win);
  if ((request?.id ?? null) !== opened) {
    setOpened(request?.id ?? null);
    setDate(start.date);
    setWin(start.win);
  }
  const save = useSaveKey(`${request?.id}:${date}:${win}`);
  const picked = new Set(request?.times.map((t) => t.date) ?? []);
  return (
    <FormDialog
      isOpen={!!request}
      onClose={onClose}
      title={request ? `Move ${request.customer.name}’s visit` : ''}
      submitLabel="Move visit"
      isDirty={date !== start.date || win !== start.win}
      note={
        request
          ? `Now: ${request.visit.whenText}.${
              request.times.length ? ` They asked for ${request.times.map((t) => t.text).join('; ')}.` : ''
            }`
          : null
      }
      onSubmit={async () => {
        if (!request) return;
        try {
          await moveVisit(request, date, win, timeZone, save.key);
        } catch (e) {
          throw new Error(requestError(e), { cause: e });
        }
        const day = days.find((d) => d.date === date)?.label ?? date;
        onMoved(request, `${day}, ${windowLabel(DAY_WINDOWS[win]!)}`);
      }}
    >
      <SelectField
        label="Day"
        value={date}
        onChange={setDate}
        options={days.map((d) => ({
          value: d.date,
          label: `${d.label}${picked.has(d.date) ? ' (they asked)' : ''}`,
        }))}
      />
      <SelectField
        label="Arrival window"
        value={String(win)}
        onChange={(v) => setWin(Number(v))}
        options={DAY_WINDOWS.map((w, i) => ({ value: String(i), label: windowLabel(w) }))}
      />
    </FormDialog>
  );
}
